/**
 * Centered "not authorized" banner used by route guards and any page that
 * needs to show a permission-denied state inline (e.g. when an API returns
 * 403 for a specific resource).
 */

import React from 'react'
import { ShieldAlert } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { logger } from '../../utils/logger'

export const UnauthorizedBanner: React.FC = () => {
  const { logout } = useAuth()

  const handleLogout = async () => {
    try {
      await logout()
    } catch (err) {
      logger.error('Failed to logout', err instanceof Error ? err : undefined)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        padding: '2rem',
        width: '100%',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          maxWidth: 380,
          width: '100%',
          padding: '2.5rem 2rem',
          borderRadius: 12,
          border: '1px solid var(--stroke, #e2e8f0)',
          background: 'var(--surface, #ffffff)',
          boxShadow:
            '0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px -12px rgba(0, 0, 0, 0.12)',
        }}
      >
        {/* Status icon — static brand-tinted badge, inherits color via currentColor */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            color: 'var(--primary, #ff6600)',
            background: 'rgba(255, 102, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem',
          }}
        >
          <ShieldAlert size={26} strokeWidth={1.75} aria-hidden />
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--text, #111827)',
            margin: '0 0 0.5rem',
            lineHeight: 1.3,
          }}
        >
          Access denied
        </h2>

        {/* Description */}
        <p
          style={{
            color: 'var(--text-muted, #6c757d)',
            fontSize: '0.875rem',
            margin: '0 0 1.75rem',
            lineHeight: 1.55,
          }}
        >
          You don&apos;t have permission to view this page. Please contact your
          administrator to request access.
        </p>

        {/* Secondary action: only self-service route when signed in without access */}
        <button
          type="button"
          className="btn btn--primary"
          onClick={handleLogout}
          style={{
            width: '100%',
            height: 38,
            justifyContent: 'center',
            fontSize: '0.875rem',
          }}
        >
          Sign in with a different account
        </button>
      </div>
    </div>
  )
}

export default UnauthorizedBanner
